import React, { useState, useEffect } from "react";
import { Link, useLocation } from "wouter";
import { 
  LayoutDashboard, 
  Users, 
  Scale, 
  FileText, 
  Calendar,
  Settings,
  Truck,
  Camera,
  LogOut,
  Menu,
  Activity,
  History,
  ShoppingCart,
  Bell,
  AlertTriangle,
  X,
  ChevronDown,
  ChevronRight,
  Building2,
  UserCheck,
  CheckCircle,
  TrendingUp,
  BarChart3,
  Package,
  Search,
  User,
  Moon,
  Sun,
  Globe,
  HelpCircle,
  Shield
} from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "./ui/collapsible";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "./ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { useToast } from "../hooks/use-toast";
import { queryClient } from "../lib/queryClient";
import { getCurrentUser, getAuthToken, getTenantId, clearAuth } from "../lib/auth";

// navigation structure for regular users
const REGULAR_NAVIGATION = [
  {
    id: "dashboard",
    label: "Dashboard",
    icon: LayoutDashboard,
    path: "/",
    type: "single"
  },
  {
    id: "operations",
    label: "Operations",
    icon: Truck,
    type: "group",
    items: [
      { label: "CCTV", path: "/cctv", icon: Camera },
      { label: "Loading Board", path: "/kanban-board", icon: Activity },
      { label: "Access Registry", path: "/transportation", icon: Truck },
      { label: "Stock Management", path: "/stock-management", icon: Package },
      { label: "Orders", path: "/orders", icon: ShoppingCart }
    ]
  },
  {
    id: "weighbridge",
    label: "Weighbridge",
    icon: Scale,
    type: "group",
    items: [
      { label: "Sessions", path: "/sessions", icon: Scale },
      { label: "Tickets", path: "/tickets", icon: FileText },
      { label: "History", path: "/audit", icon: History }
    ]
  },
  {
    id: "visits",
    label: "Visits & Access",
    icon: Users,
    type: "group",
    items: [
      { label: "Visits", path: "/visits", icon: Users },
      { label: "Visitor Compliance", path: "/visitor-compliance", icon: UserCheck },
      { label: "Allowance", path: "/allowance", icon: CheckCircle },
      { label: "Bookings", path: "/bookings", icon: Calendar },
      { label: "Plan vs Actual", path: "/plan-vs-actual", icon: TrendingUp }
    ]
  },
  {
    id: "business",
    label: "Business",
    icon: Building2,
    type: "group",
    items: [
      { label: "Partners", path: "/business-partners", icon: Users }
    ]
  },
  {
    id: "reports",
    label: "Reports",
    icon: BarChart3,
    type: "group",
    items: [
      { label: "Scheduled Reports", path: "/scheduled-reports", icon: Calendar },
      { label: "Overview", path: "/overview", icon: BarChart3 }
    ]
  },
  {
    id: "settings",
    label: "Settings",
    icon: Settings,
    path: "/settings",
    type: "single"
  }
];

// navigation structure for super admin
const SUPER_ADMIN_NAVIGATION = [
  {
    id: "user-management",
    label: "User Management",
    icon: Shield,
    path: "/admin",
    type: "single"
  },
  {
    id: "business",
    label: "Business",
    icon: Building2,
    type: "group",
    items: [
      { label: "Partners", path: "/business-partners", icon: Users },
      { label: "Suppliers", path: "/suppliers", icon: Building2 },
      { label: "Clients", path: "/clients", icon: Users },
      { label: "Transporters", path: "/transporters", icon: Truck }
    ]
  },
  {
    id: "reports",
    label: "Reports",
    icon: BarChart3,
    type: "group",
    items: [
      { label: "Scheduled Reports", path: "/scheduled-reports", icon: Calendar },
      { label: "Overview", path: "/overview", icon: BarChart3 }
    ]
  },
  {
    id: "settings",
    label: "Settings",
    icon: Settings,
    path: "/settings",
    type: "single"
  }
];

interface AppShellProps {
  children: React.ReactNode;
}

export function AppShell({ children }: AppShellProps) {
  const [location] = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isEmergencyActive, setIsEmergencyActive] = useState(false);
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});
  const [searchQuery, setSearchQuery] = useState("");
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem('darkMode');
    return saved === 'true';});
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(true);
  const { toast } = useToast();
  
  // Get current logged-in user
  const currentUser = getCurrentUser();
  const isSuperAdmin = currentUser?.role === 'super_admin';
  const isPortUser = currentUser?.role === 'port';
  
  let NAVIGATION;
  if (isSuperAdmin) {
    NAVIGATION = SUPER_ADMIN_NAVIGATION;
  } else if (isPortUser) {
    NAVIGATION = REGULAR_NAVIGATION.map(nav => {
      if (nav.id === 'weighbridge') {
        return {
          id: "weighbridge",
          label: "Weighbridge",
          icon: Scale,
          type: "group" as const,
          items: [
            { label: "Sessions", path: "/sessions", icon: Scale },
            { label: "Internal Weighbridge", path: "/internal-weighbridge", icon: Scale },
            { label: "External Weighbridge", path: "/external-weighbridge", icon: FileText },
            { label: "History", path: "/audit", icon: History }
          ]
        };
      }
      return nav;
    });
  } else {
    NAVIGATION = REGULAR_NAVIGATION;
  }

  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, []);

  // Initialize open groups from localStorage and auto-expand current route
  useEffect(() => {
    const saved = localStorage.getItem('sidebar.openGroups');
    const savedState = saved ? JSON.parse(saved) : {};
    
    // Auto-open group that contains the current route
    const autoOpenState = { ...savedState };
    NAVIGATION.forEach(nav => {
      if (nav.type === 'group' && nav.items) {
        const hasActiveRoute = nav.items.some(item => item.path === location);
        if (hasActiveRoute) {
          autoOpenState[nav.id] = true;
        }
      }
    });
    
    setOpenGroups(autoOpenState);
  }, [location]);

  // Persist openGroups to localStorage
  useEffect(() => {
    localStorage.setItem('sidebar.openGroups', JSON.stringify(openGroups));
  }, [openGroups]);

  // Auto-collapse all submenus when sidebar collapses
  useEffect(() => {
    if (isSidebarCollapsed) {
      setOpenGroups({});
    } else {
      // When sidebar expands, auto-open group that contains the current route
      const autoOpenState: Record<string, boolean> = {};
      NAVIGATION.forEach(nav => {
        if (nav.type === 'group' && nav.items) {
          const hasActiveRoute = nav.items.some(item => item.path === location);
          if (hasActiveRoute) {
            autoOpenState[nav.id] = true;
          }
        }
      });
      setOpenGroups(autoOpenState);
    }
  }, [isSidebarCollapsed, location]);

  const toggleGroup = (groupId: string) => {
    setOpenGroups(prev => ({
      ...prev,
      [groupId]: !prev[groupId]
    }));
  };

  const [, setLocation] = useLocation();

  const handleLogout = async () => {
    try {
      await fetch("/api/auth/logout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include"
      });
    } catch (error) {
      console.log("Logout error (ignored):", error);
    }

    queryClient.clear();
    localStorage.clear();
    sessionStorage.clear();
    setLocation("/login");

    toast({
      title: "Logged out successfully",
      description: "You have been logged out of the system.",
    });

    window.location.href = "/login";
  };

  const handleEmergencyStop = async () => {
    try {
      setIsEmergencyActive(true);
      
      const token = getAuthToken();
      const tenantId = getTenantId();
      
      const response = await fetch('/api/emergency-stop', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'X-Tenant-Id': tenantId || ''
        }
      });

      if (response.ok) {
        toast({
          title: "🚨 EMERGENCY STOP ACTIVATED",
          description: "All weighbridge operations have been halted. System is in safe mode.",
          variant: "destructive",
        });
      } else {
        throw new Error('Emergency stop failed');
      }
    } catch (error) {
      toast({
        title: "Emergency Stop Error",
        description: "Failed to activate emergency stop. Contact system administrator.",
        variant: "destructive",
      });
      setIsEmergencyActive(false);
    }
  };

  const handleEmergencyReset = async () => {
    try {
      const token = getAuthToken();
      const tenantId = getTenantId();
      
      const response = await fetch('/api/emergency-reset', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'X-Tenant-Id': tenantId || ''
        }
      });

      if (response.ok) {
        setIsEmergencyActive(false);
        toast({
          title: "System Reset",
          description: "Emergency stop cleared. Operations can resume.",
          variant: "default",
        });
      }
    } catch (error) {
      toast({
        title: "Reset Error", 
        description: "Failed to reset emergency stop. Manual intervention required.",
        variant: "destructive",
      });
    }
  };

  const handleNotifications = () => {
    toast({
      title: "Notifications", 
      description: "No new notifications at this time.",
    });
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      toast({
        title: "Search",
        description: `Searching for: ${searchQuery}`,
      });
      // TODO: Implement actual search functionality
    }
  };

  const toggleDarkMode = () => {
    const newMode = !isDarkMode;
    setIsDarkMode(newMode);
    localStorage.setItem('darkMode', newMode.toString());
    if (newMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    
    toast({
      title: "Theme Changed",
      description: `Switched to ${newMode ? 'dark' : 'light'} mode`,
    });
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  const SidebarContent = ({ isMobile = false, isCollapsed = false }: { isMobile?: boolean; isCollapsed?: boolean }) => (
    <div className="flex flex-col h-full">
      {/* Logo & Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-700">
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 bg-slate-600 rounded-lg flex items-center justify-center">
            <Scale className="w-4 h-4 text-white" />
          </div>
          {(!isCollapsed || isMobile) && (
            <div>
              <h1 className="text-lg font-bold text-white">WEIGH8</h1>
              <p className="text-xs text-gray-300">Automated Weighbridge</p>
            </div>
          )}
        </div>
        {isMobile && (
          <Button variant="ghost" size="icon" onClick={closeMobileMenu}>
            <X className="w-4 h-4" />
          </Button>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        {NAVIGATION.map((nav) => {
          if (nav.type === 'single') {
            const isActive = location === nav.path;
            return (
              <Link key={nav.id} href={nav.path!} onClick={isMobile ? closeMobileMenu : undefined}>
                <div 
                  className={`flex items-center px-3 py-2 rounded-lg font-medium transition-colors cursor-pointer ${
                    isActive 
                      ? "bg-slate-700 text-white" 
                      : "text-gray-300 hover:bg-slate-700/50 hover:text-white"
                  }`}
                  data-testid={`nav-${nav.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <nav.icon className="w-5 h-5 mr-3" />
                  {(!isCollapsed || isMobile) && nav.label}
                </div>
              </Link>
            );
          }

          if (nav.type === 'group' && nav.items) {
            const hasActiveRoute = nav.items.some(item => item.path === location);
            const isOpen = openGroups[nav.id];

            return (
              <div key={nav.id} className="space-y-1">
                <Collapsible
                  open={isOpen}
                  onOpenChange={() => toggleGroup(nav.id)}
                >
                  <CollapsibleTrigger asChild>
                    <Button
                      variant="ghost"
                      className={`w-full justify-between font-medium px-3 py-2 ${
                        hasActiveRoute 
                          ? 'bg-slate-700 text-white' 
                          : 'text-gray-300 hover:bg-slate-700/50 hover:text-white'
                      }`}
                      data-testid={`nav-group-${nav.id}`}
                    >
                      <div className="flex items-center">
                        <nav.icon className="w-5 h-5 mr-3" />
                        {(!isCollapsed || isMobile) && <span>{nav.label}</span>}
                      </div>
                      {(!isCollapsed || isMobile) && (isOpen ? (
                        <ChevronDown className="w-4 h-4" />
                      ) : (
                        <ChevronRight className="w-4 h-4" />
                      ))}
                    </Button>
                  </CollapsibleTrigger>
                  <CollapsibleContent className={`space-y-1 ${isCollapsed && !isMobile ? 'pl-0' : 'pl-4'}`}>
                    {nav.items.map((item) => {
                      const isActive = location === item.path;
                      return (
                        <Link key={item.path} href={item.path} onClick={isMobile ? closeMobileMenu : undefined}>
                          <div 
                            className={`flex items-center px-3 py-2 rounded-lg font-medium transition-colors cursor-pointer ${
                              isActive 
                                ? "bg-slate-700 text-white" 
                                : "text-gray-400 hover:bg-slate-700/50 hover:text-white"
                            }`}
                            data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                          >
                            <item.icon className="w-4 h-4 mr-3" />
                            {(!isCollapsed || isMobile) && item.label}
                          </div>
                        </Link>
                      );
                    })}
                  </CollapsibleContent>
                </Collapsible>
              </div>
            );
          }

          return null;
        })}
      </nav>

      {/* User Profile & Actions */}
      <div className="border-t border-gray-700 p-4 space-y-3">
        {/* Emergency Controls */}
        <div className="space-y-2">
          {!isEmergencyActive ? (
            <Button 
              variant="destructive" 
              size={isCollapsed && !isMobile ? "icon" : "sm"}
              className={isCollapsed && !isMobile ? "w-full" : "w-full"}
              onClick={handleEmergencyStop}
              data-testid="button-emergency-stop"
              title={isCollapsed && !isMobile ? "Emergency Stop" : undefined}
            >
              <AlertTriangle className="w-4 h-4" />
              {(!isCollapsed || isMobile) && (
                <>
                  <span className="ml-2">Emergency Stop</span>
                </>
              )}
            </Button>
          ) : (
            <Button 
              variant="outline" 
              size={isCollapsed && !isMobile ? "icon" : "sm"}
              className={`w-full ${isCollapsed && !isMobile ? '' : 'border-orange-300 text-orange-700 hover:bg-orange-50'}`}
              onClick={handleEmergencyReset}
              data-testid="button-emergency-reset"
              title={isCollapsed && !isMobile ? "Reset System" : undefined}
            >
              <AlertTriangle className="w-4 h-4" />
              {(!isCollapsed || isMobile) && (
                <>
                  <span className="ml-2">Reset System</span>
                </>
              )}
            </Button>
          )}
          
          <Button 
            variant="outline" 
            size={isCollapsed && !isMobile ? "icon" : "sm"}
            className="w-full"
            onClick={handleNotifications}
            data-testid="button-notifications"
            title={isCollapsed && !isMobile ? "Notifications" : undefined}
          >
            <Bell className="w-4 h-4" />
            {(!isCollapsed || isMobile) && (
              <>
                <span className="ml-2">Notifications</span>
              </>
            )}
          </Button>
        </div>

        {/* User Profile */}
        <div className="flex items-center space-x-3 p-2 rounded-lg bg-slate-800">
          <div className="w-8 h-8 bg-slate-600 rounded-full flex items-center justify-center">
            <span className="text-sm font-medium text-white">
              {currentUser?.firstName && currentUser?.lastName 
                ? `${currentUser.firstName[0]}${currentUser.lastName[0]}`
                : currentUser?.email 
                  ? currentUser.email.substring(0, 2).toUpperCase()
                  : 'U'}
            </span>
          </div>
          {(!isCollapsed || isMobile) && (
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-white truncate">
                {currentUser?.firstName && currentUser?.lastName 
                  ? `${currentUser.firstName} ${currentUser.lastName}`
                  : currentUser?.username || 'User'}
              </div>
              <div className="text-xs text-gray-300">
                {currentUser?.role === 'super_admin' ? 'Super Administrator' : 
                 currentUser?.role === 'admin' ? 'Administrator' : 
                 currentUser?.role === 'operator' ? 'Operator' : 'User'}
              </div>
            </div>
          )}
          <Button
            variant="ghost"
            size="icon"
            onClick={handleLogout}
            className="text-gray-400 hover:text-white"
            data-testid="button-logout"
            title="Logout"
          >
            <LogOut className="w-4 h-4" />
          </Button>
        </div>

        {/* System Status */}
        <div className={`flex items-center justify-center space-x-2 px-3 py-2 rounded-lg text-sm ${
          isEmergencyActive 
            ? 'bg-red-900/20 text-red-400'
            : 'bg-slate-800 text-gray-300'
        }`}>
          <span className={`w-2 h-2 rounded-full ${isEmergencyActive ? 'bg-red-500' : 'bg-green-500'}`}></span>
          {(!isCollapsed || isMobile) && (
            <span className="font-medium">
              {isEmergencyActive ? 'EMERGENCY MODE' : 'SYSTEM ONLINE'}
            </span>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex bg-gray-50 dark:bg-gray-900">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex lg:flex-shrink-0">
        <div 
          className={`flex flex-col ${isSidebarCollapsed ? 'w-16' : 'w-64'} bg-slate-900 border-r border-gray-700 transition-all duration-300 ease-in-out`}
          onMouseEnter={() => setIsSidebarCollapsed(false)}
          onMouseLeave={() => setIsSidebarCollapsed(true)}
        >
          <SidebarContent isCollapsed={isSidebarCollapsed} />
        </div>
      </aside>

      {/* Mobile Sidebar */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="fixed inset-0 bg-black bg-opacity-50" onClick={closeMobileMenu}></div>
          <div className="fixed left-0 top-0 bottom-0 w-64 bg-slate-900 shadow-xl">
            <SidebarContent isMobile={true} />
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top Header Bar */}
        <header className="bg-slate-800 border-b border-gray-700 px-4 lg:px-6 py-3">
          <div className="flex items-center justify-between">
            {/* Left: Mobile menu + Search */}
            <div className="flex items-center space-x-4 flex-1">
              <Button 
                variant="ghost" 
                size="icon"
                className="lg:hidden"
                onClick={() => setIsMobileMenuOpen(true)}
                data-testid="button-mobile-menu"
              >
                <Menu className="w-5 h-5" />
              </Button>
              
              {/* Search */}
              <form onSubmit={handleSearch} className="hidden md:flex items-center space-x-2 max-w-md flex-1">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <Input
                    type="search"
                    placeholder="Search sessions, tickets, visits..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 pr-4 py-2 w-full bg-slate-700 border-slate-600 text-white placeholder:text-gray-400"
                    data-testid="input-search"
                  />
                </div>
              </form>
            </div>

            {/* Center: Mobile logo (hidden on desktop) */}
            <div className="flex items-center space-x-2 lg:hidden">
              <div className="w-6 h-6 bg-slate-600 rounded flex items-center justify-center">
                <Scale className="w-3 h-3 text-white" />
              </div>
              <span className="font-bold text-white">WEIGH8</span>
            </div>

            {/* Right: Actions & User Profile */}
            <div className="flex items-center space-x-2">
              {/* Emergency Status */}
              <div className={`hidden sm:flex items-center space-x-2 px-3 py-1 rounded-full text-xs font-medium ${
                isEmergencyActive 
                  ? 'bg-red-900/20 text-red-400'
                  : 'bg-slate-700 text-gray-300'
              }`}>
                <span className={`w-2 h-2 rounded-full ${isEmergencyActive ? 'bg-red-500' : 'bg-green-500'}`}></span>
                <span>{isEmergencyActive ? 'EMERGENCY' : 'ONLINE'}</span>
              </div>

              {/* Notifications */}
              <Button
                variant="ghost"
                size="icon"
                onClick={handleNotifications}
                className="relative"
                data-testid="button-notifications"
              >
                <Bell className="w-5 h-5" />
                <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full"></span>
              </Button>

              {/* Dark Mode Toggle */}
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleDarkMode}
                data-testid="button-theme-toggle"
              >
                {isDarkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </Button>

              {/* User Profile Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="relative h-10 w-10 rounded-full" data-testid="button-user-menu">
                    <Avatar className="h-10 w-10">
                      <AvatarImage src="" alt="User" />
                      <AvatarFallback className="bg-slate-600 text-white">
                        {currentUser?.firstName && currentUser?.lastName 
                          ? `${currentUser.firstName[0]}${currentUser.lastName[0]}`
                          : currentUser?.email 
                            ? currentUser.email.substring(0, 2).toUpperCase()
                            : 'U'}
                      </AvatarFallback>
                    </Avatar>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-56" align="end" forceMount>
                  <div className="flex flex-col space-y-1 p-2">
                    <p className="text-sm font-medium leading-none">
                      {currentUser?.firstName && currentUser?.lastName 
                        ? `${currentUser.firstName} ${currentUser.lastName}`
                        : currentUser?.username || 'User'}
                    </p>
                    <p className="text-xs leading-none text-muted-foreground">
                      {currentUser?.role === 'super_admin' ? 'Super Administrator' : 
                       currentUser?.role === 'admin' ? 'Administrator' : 
                       currentUser?.role === 'operator' ? 'Operator' : 'User'}
                    </p>
                    <p className="text-xs leading-none text-muted-foreground">{currentUser?.email || ''}</p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem data-testid="menu-profile">
                    <User className="mr-2 h-4 w-4" />
                    <span>Profile</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem data-testid="menu-settings">
                    <Settings className="mr-2 h-4 w-4" />
                    <span>Settings</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem data-testid="menu-help">
                    <HelpCircle className="mr-2 h-4 w-4" />
                    <span>Help & Support</span>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} data-testid="menu-logout">
                    <LogOut className="mr-2 h-4 w-4" />
                    <span>Log out</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>

          {/* Mobile Search */}
          <form onSubmit={handleSearch} className="md:hidden mt-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                type="search"
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 pr-4 py-2 w-full bg-slate-700 border-slate-600 text-white placeholder:text-gray-400"
                data-testid="input-search-mobile"
              />
            </div>
          </form>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-auto">
          <div className="p-6">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}

export default AppShell;